/**
 * Production Issues Types (Frontend)
 */
import type {
  BridgeFetchState,
  LastSeenPhase,
  PrintFileOpenCategory,
} from '@threadheads/shared';

/**
 * BLU-695 — `redo_reason` stamped on a top-up unit minted for an order
 * change. Mirrors the backend const (lib/garment-status.ts); the packing
 * feature imports it from here too rather than re-typing the literal.
 */
export const ORDER_CHANGE_BACKFILL_REASON = 'order_change_backfill';

export type IssueType =
  | 'OOS_PRE_BATCH'
  | 'INVENTORY_ERROR'
  | 'PICKING_DEFECTIVE'
  | 'REDO_PENDING'
  | 'STUCK_BIN'
  | 'PAP_DAMAGED'
  | 'PRINT_DEFECT'
  | 'PRINT_WRONG_ITEM'
  | 'PRINT_ERROR'
  | 'PRINT_FILE_ISSUE'
  | 'SPLIT_ORDER'
  | 'ORDER_ITEM_REMOVED'
  | 'ORDER_CHANGED'
  | 'SKU_NOT_FOUND';

/**
 * Issue types that represent Out-of-Stock conditions
 */
export const OOS_ISSUE_TYPES: readonly IssueType[] = [
  'OOS_PRE_BATCH',
  'INVENTORY_ERROR',
] as const;

/**
 * Check if an issue type is an OOS-related type
 */
export function isOOSIssue(issueType: IssueType): boolean {
  return OOS_ISSUE_TYPES.includes(issueType);
}

export type IssueStatus = 'OPEN' | 'RESOLVED' | 'ROUTED' | 'CANCELLED';

export type ResolutionMethod =
  | 'auto_resume'
  | 'manual'
  | 'routed'
  | 'cancelled'
  | 'bridge_refetch' // Phase 60: bootleg-bridge re-fetched the source artwork (no JWT user)
  | 'order_terminal' // order reached a terminal state (shipped/fulfilled/cancelled/closed) → auto-cleared by order-sync
  | 'designs_source' // BLU-590: hands-off heal fetched the design PNG from the org's Google Drive designs folder
  | 'custom_source' // Self-Heal Now: server-side custom heal re-fetched the order item's source artwork → platen resize → ingest (deferred-confirm path has no JWT user)
  | 'coverage_healed' // BLU-695: the order-coverage cron re-evaluated the order and the drift is gone → ORDER_CHANGED auto-cleared (no JWT user)
  | 'standards_metafield' // BLU-827: server-side STANDARDS heal wrote the .png direct from the variant's front/back artwork-link metafield (no JWT user). Bridge-produced standards resolves stay 'bridge_refetch'.
  | 'duplicate'; // BLU-1333: closed by migration 20261009100000 as a duplicate of an older OPEN issue on the same (org, order, sku, type). DB-only, never sent by the resolve dialog; the row's notes say which issue it duplicated.

export interface ProductionIssue {
  id: string;
  organization_id: string;
  location_id: string;
  issue_type: IssueType;
  status: IssueStatus;
  prod_order_id: string | null;
  garment_unit_id: string | null;
  bin_id: string | null;
  batch_id: string | null;
  blocked_sku: string | null;
  blocked_quantity: number | null;
  deficit: number | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_method: ResolutionMethod | null;
  routed_to_location_id: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Joined fields
  order_name?: string;
  /**
   * BLU-563 R3: the linked ERP `orders.id` (joined via prod_orders.order_id in
   * listIssues). The split-order quick actions (Accept split / Consolidate to)
   * mutate by THIS id, NOT prod_order_id. Null when the prod_order has no
   * linked `orders` row — the quick actions hide themselves then.
   */
  order_id?: string | null;
  /** Masked customer display (e.g., "J.S." or "j***@example.com") - PII minimized server-side */
  customer_display?: string;
  location_name?: string;
  /**
   * ORDER_CHANGED rows only: the stored title, colour and size of the row's
   * own order lines, so the card can say which garment changed. Attached by
   * listIssues from `order_items`, keyed by the line ids in `metadata.lines`.
   * Absent on every other issue type, on a server one deploy behind, and for
   * any line that could not be named: the card then shows the SKU instead.
   */
  order_change_line_display?: Array<{
    orderItemId: string;
    productName: string | null;
    variantTitle: string | null;
    color: string | null;
    size: string | null;
  }>;
  /**
   * Phase 60-05: derived server-side on the issues.list read — true when this is
   * an OPEN file_missing print-file issue whose order_item carries a re-fetchable
   * artwork URL, so the bootleg-bridge is auto-fetching the source. Drives the
   * "Auto-fetching from source" pill + the demoted "Upload manually" CTA. The
   * artwork URL itself is never sent to the client (presigned creds).
   */
  bridgeEligible?: boolean;
  /** Times the bridge queue served this issue without a resolve. */
  bridgeFetchAttempts?: number;
  /** True when bridge-eligible AND the attempt counter reached the queue's
   *  poison cap — the bridge will never retry; the card shows "Auto-heal
   *  failed" + Self-Heal Now / manual upload instead of the fetching pill. */
  bridgePoisoned?: boolean;
  /**
   * BLU-880: live bridge-fetch lifecycle projected from the issue's
   * `bridge_fetch_state` stamps (`queued` → `served` → `downloaded` →
   * `ripped` → `uploaded` | `failed`), plus BLU-663's `awaiting_bridge` —
   * the work is HELD until a daemon that can rip both sides checks in, which
   * is not a step in the fetch pipeline and never seeds the stepper
   * (`snapshotFromFields` rejects it); the list renders its own explicit
   * "waiting for a capable bridge" pill instead of the auto-fetch spinner.
   * Null/absent when nothing ever stamped the row. The same three keys also
   * stay in `metadata` for the single-row read.
   */
  bridgeFetchState?: BridgeFetchState | null;
  bridgeFetchStateAt?: string | null;
  bridgeFetchFailReason?: string | null;
  /**
   * Manual/Self-Healing category for the resolved view — the SAME server-side
   * one-way ratchet as the print-station fixed feed (derived from the issue's
   * filed-notification state in listIssues): 'self_heal' = healed silently
   * end-to-end before anyone was notified; 'manual' = operator-known (filed,
   * or auto-fetch exhausted and Slack fired before the fix) or unstamped
   * legacy rows. Null for non-RESOLVED rows; absent on non-print-file issues
   * and stale-cached rows — treat anything that isn't exactly 'self_heal' as
   * Manual (never hide something an operator may have known about).
   */
  resolvedCategory?: 'manual' | 'self_heal' | null;
  /**
   * Open-view classification — server-derived (`deriveOpenCategory`) from the
   * same signals the automation itself reads:
   *   'auto_fetching'   the bridge / custom-source heal / sweep retry is still
   *                     working the issue — watch, don't race it;
   *   'awaiting_bridge' (BLU-663) nothing is working it YET: the file needs a
   *                     daemon that can rip front AND back and none has
   *                     checked in. Not a spinner — an explicit wait pill;
   *   'needs_manual'    every automation path exhausted (or nothing tried).
   * Gates the "Auto-fetching from source" pill — `bridgeEligible` alone kept
   * it spinning forever on sweep-exhausted rows. Null for non-OPEN rows;
   * absent on non-print-file issues and stale caches (treat absent as
   * not-auto-fetching — never show a calm spinner we can't back up).
   */
  openCategory?: PrintFileOpenCategory | null;
  /**
   * Phase 86: this size prints only from its own platen variant file, which
   * is uploaded from Product updates, never from the issue. The card hides
   * the issue upload (and bulk selection) and links to Product updates.
   * Server-derived from the issue's own marker; absent means false.
   */
  platenVariantFileNeeded?: boolean;
  // ── Print-file issue display enrichment (PRINT_FILE_ISSUE rows only) ──
  // Resolved server-side in listIssues; the card renders the preview + Shopify
  // links from these (links built client-side with organizations.shopify_domain).
  /** Live product preview (signed custom preview, else product image). */
  preview_image_url?: string | null;
  /** Design/product title for the card heading. */
  design_product_name?: string | null;
  /** Design-bearing SKU (order_items.shopify_sku) shown in the badge. */
  design_sku?: string | null;
  size?: string | null;
  color?: string | null;
  variant_title?: string | null;
  /** For the Shopify admin order link. */
  shopify_order_id?: number | null;
  /** For the Shopify admin product/variant link. */
  shopify_product_id?: number | null;
  /** For the Shopify admin variant link. */
  shopify_variant_id?: number | null;
}

export interface IssueCounts {
  oos_total: number;
  oos_order_count: number;
  redo_pending: number;
  stuck_bins: number;
  pap_damaged: number;
  print_file_issues: number;
  /**
   * The Print Files tab's open-view chip counts: automation still working
   * (`auto_fetching`), held for a bridge that can rip both sides
   * (`awaiting_bridge`, BLU-663), or the operator's problem now
   * (`needs_manual`). The three partition `print_file_issues`. Classified
   * over a bounded (1000-row) fetch server-side, so at a pathological
   * location they may undercount relative to the exact total.
   */
  print_file_auto_fetching: number;
  print_file_awaiting_bridge: number;
  print_file_needs_manual: number;
  split_orders: number;
  resolved_ready_count: number;
  cancelled_count: number;
}

/**
 * Grouped OOS issues by order.
 * Multiple OOS SKU issues for the same order are collapsed into one group.
 */
export interface OrderIssueGroup {
  prod_order_id: string;
  order_name: string | undefined;
  customer_display: string | undefined;
  /** Oldest issue — used for modal/resolve actions */
  primaryIssue: ProductionIssue;
  /** All OOS issues for this order */
  issues: ProductionIssue[];
  /** Summary of blocked SKUs with deficits */
  blockedSkus: Array<{ sku: string; deficit: number | null }>;
  /** Earliest created_at across all issues */
  createdAt: string;
  /** Whether any issue has awaiting_inventory metadata */
  hasAwaitingInventory: boolean;
  /** Whether any issue has been CX-contacted (template or manual) — drives the list-view badge */
  hasContacted: boolean;
}

/**
 * Issue type display config
 */
export const issueTypeConfig: Record<
  IssueType,
  { label: string; color: string; icon: string }
> = {
  OOS_PRE_BATCH: {
    label: 'Pre-Batch OOS',
    color: 'text-red-600 bg-red-50',
    icon: 'PackageX',
  },
  INVENTORY_ERROR: {
    label: 'Stock Issue',
    color: 'text-amber-600 bg-amber-50',
    icon: 'PackageX',
  },
  PICKING_DEFECTIVE: {
    label: 'Picking Defective',
    color: 'text-red-600 bg-red-50',
    icon: 'AlertTriangle',
  },
  REDO_PENDING: {
    label: 'Redo Pending',
    color: 'text-amber-600 bg-amber-50',
    icon: 'RotateCcw',
  },
  STUCK_BIN: {
    label: 'Stuck Bin',
    color: 'text-amber-600 bg-amber-50',
    icon: 'Archive',
  },
  PAP_DAMAGED: {
    label: 'P&P Damaged',
    color: 'text-red-600 bg-red-50',
    icon: 'XCircle',
  },
  PRINT_DEFECT: {
    label: 'Print Defect',
    color: 'text-red-600 bg-red-50',
    icon: 'AlertTriangle',
  },
  PRINT_WRONG_ITEM: {
    label: 'Wrong Item (Print)',
    color: 'text-amber-600 bg-amber-50',
    icon: 'AlertTriangle',
  },
  PRINT_ERROR: {
    label: 'Print Error',
    color: 'text-red-600 bg-red-50',
    icon: 'AlertTriangle',
  },
  PRINT_FILE_ISSUE: {
    label: 'Print File Issue',
    color: 'text-orange-600 bg-orange-50',
    icon: 'FileWarning',
  },
  SPLIT_ORDER: {
    label: 'Split Order',
    color: 'text-purple-600 bg-purple-50',
    icon: 'GitBranch',
  },
  ORDER_ITEM_REMOVED: {
    label: 'Item Removed',
    color: 'text-orange-600 bg-orange-50',
    icon: 'FileEdit',
  },
  ORDER_CHANGED: {
    label: 'Order Changed',
    color: 'text-orange-600 bg-orange-50',
    icon: 'FileEdit',
  },
  SKU_NOT_FOUND: {
    label: 'SKU Not in Catalog',
    color: 'text-red-600 bg-red-50',
    icon: 'FileWarning',
  },
};

// ============================================================================
// Alert Banner Types
// ============================================================================

/**
 * Tab values used in IssuesPage navigation
 */
export type TabValue = 'oos' | 'redos' | 'stuck-bins' | 'cancelled' | 'on-hold' | 'shipping' | 'print-files' | 'split-orders' | 'order-changes' | 'catalog';

/**
 * Alert severity level - determines styling and priority
 */
export type AlertSeverity = 'critical' | 'warning';

/**
 * Alert banner data structure
 */
export interface Alert {
  /** Unique ID for dismissal tracking (e.g., 'oos-{locationId}-{sku}') */
  id: string;
  /** Severity determines styling (red for critical, amber for warning) */
  severity: AlertSeverity;
  /** Short title (e.g., '5 orders blocked') */
  title: string;
  /** Detailed message (e.g., 'SKU TSHIRT-BLK-XL out of stock') */
  message: string;
  /** Action button label (e.g., 'View OOS') */
  actionLabel: string;
  /** Tab to navigate to when action is clicked */
  actionTab: TabValue;
  /** Optional grouping key for consolidation (e.g., SKU) */
  groupKey?: string;
}

/**
 * Alert threshold configuration
 */
export interface AlertThresholds {
  print_defect_rate: number;
  print_defect_window_hours: number;
  inventory_error_rate: number;
  inventory_error_window_hours: number;
  pap_damage_rate: number;
  pap_damage_window_days: number;
  stuck_bin_count: number;
  stuck_bin_age_hours: number;
}

/**
 * Default alert thresholds
 */
export const DEFAULT_THRESHOLDS: AlertThresholds = {
  print_defect_rate: 5,
  print_defect_window_hours: 24,
  inventory_error_rate: 3,
  inventory_error_window_hours: 24,
  pap_damage_rate: 2,
  pap_damage_window_days: 3,
  stuck_bin_count: 5,
  stuck_bin_age_hours: 24,
};

// ============================================================================
// Cancelled Items Types
// ============================================================================

/**
 * Cancelled item type - determines action to take
 * BLANK = QR label exists but garment not yet printed (return to shelf)
 * PRINTED = Garment has been through the printer (mark as defect)
 */
export type CancelledItemType = 'BLANK' | 'PRINTED';

/**
 * Why a garment landed on the Cancelled tab — mirrors the backend
 * `CancelReasonKind` (production-issues.types.ts). Drives the "Why this
 * garment has been cancelled" panel's fallback copy when `cancel_reason_text`
 * is null.
 */
export type CancelReasonKind = 'order_change' | 'order_cancelled' | 'order_moved' | 'print_technology_switch';

// LastSeenPhase is the shared wire union (@threadheads/shared, next to
// GarmentUnitStatus) — importing it makes the card's Record<LastSeenPhase,
// string> key map an exhaustiveness check against what the backend emits.
export type { LastSeenPhase };

/**
 * Cancelled item data structure (matches backend CancelledItem type)
 */
export interface CancelledItem {
  /** garment_unit_id */
  id: string;
  /** Shopify order name (e.g., #1234) */
  order_name: string;
  /** Product SKU */
  sku: string;
  /** Product display name */
  product_name: string | null;
  /** BLANK or PRINTED based on garment status */
  item_type: CancelledItemType;
  /** Current garment status (for debugging) */
  status: string;
  /** When the garment was created */
  created_at: string;
  /** BLU-367: the row's owning org — drives the client-org chip at shared locations */
  organization_id: string;
  prod_order_id: string;
  /** Shopify admin deep-link id; null for orphaned/inaccessible orders */
  shopify_order_id: number | null;
  customer_name: string | null;
  size: string | null;
  color: string | null;
  variant_name: string | null;
  /** Product thumbnail (`skus.image_url`), org-scoped — null when not found. */
  image_url: string | null;
  /** The backup SKU this garment was made from, when its code differs from `sku`. Only the floor owner gets it. */
  pick_sku?: string;
  /** That backup SKU's code is reserved for a client: the card shows the client badge. */
  client_labelled?: true;
  /** The reserved client's name, when known. */
  client_name?: string;
  /** The winning STATUS_OVERRIDE event's `metadata.reason`, else null. */
  cancel_reason_text: string | null;
  /** See {@link CancelReasonKind}. */
  cancel_reason_kind: CancelReasonKind;
  /** See {@link LastSeenPhase} — null when no physical station applies. */
  last_seen_phase: LastSeenPhase | null;
  /** When the garment reached that phase, else null. */
  last_seen_at: string | null;
  /** Bin label for BIN-phase garments, else null. */
  bin_label: string | null;
}

/**
 * Status classification logic for cancelled items:
 * BLANK = QR label exists but garment not yet decorated (can return to shelf)
 * - Statuses: PENDING, LABEL_PRINTED, PICKED, IN_PRETREAT, PRETREATED, FILM_PRINTED
 * PRINTED = Garment has been decorated (can't return to shelf)
 * - Statuses: CAUGHT, IN_BIN, READY_TO_PACK, RELEASED, PACKED, PRESSED
 *
 * Phase 50.01.5 (DTF): PRESSED → PRINTED (the film is heat-pressed ONTO the
 * garment — it's decorated, committed). FILM_PRINTED → BLANK: at FILM_PRINTED
 * the GARMENT is still an undecorated blank (only the separate film is printed),
 * so it remains returnable; the film is the discrete waste. This matches the
 * GARMENT_TO_PHASE mapping (FILM_PRINTED = a pre-press, PICKED-equivalent phase).
 */
export const PRINTED_STATUSES = [
  'CAUGHT',
  'IN_BIN',
  'READY_TO_PACK',
  'RELEASED',
  'PACKED',
  'PRESSED',
] as const;

export const BLANK_STATUSES = [
  'PENDING',
  'LABEL_PRINTED',
  'PICKED',
  'IN_PRETREAT',
  'PRETREATED',
  'FILM_PRINTED',
] as const;

/**
 * Determine item type from garment status
 */
export function getItemType(status: string): CancelledItemType {
  return (PRINTED_STATUSES as readonly string[]).includes(status)
    ? 'PRINTED'
    : 'BLANK';
}

/**
 * Display configuration for cancelled item types — colours ONLY.
 * Rendered strings (labels, action verbs) live in production.json
 * (`issues.cancelled.*`) so they translate; this config stays i18n-free.
 * The icon is chosen by the card from real lucide components, so no icon
 * name is carried here.
 */
export const cancelledItemConfig: Record<
  CancelledItemType,
  {
    color: string;
    bgColor: string;
  }
> = {
  BLANK: {
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  PRINTED: {
    color: 'text-red-600',
    bgColor: 'bg-red-50',
  },
};

// ============================================================================
// Order Detail Types (for OrderDetailModal)
// ============================================================================

/**
 * Stock level at a location for routing suggestions
 */
export interface StockLevel {
  location_id: string;
  location_name: string;
  available_quantity: number;
  is_current_location: boolean;
}

/**
 * Line item detail for order detail view.
 *
 * Hand-maintained mirror of the backend `LineItemDetail`
 * (apps/backend/src/modules/production-issues/production-issues.types.ts). Keep
 * the optional fields below in sync with that source.
 */
export interface LineItemDetail {
  id: string;
  sku: string;
  shopify_sku: string | null;
  product_name: string;
  quantity: number;
  status: string | null;
  is_oos: boolean;
  /**
   * BLU-563: Shopify fulfilment-order line-item id (digits). The resolver
   * (OrderResolutionFlow) sends THIS as `resolveOrderFulfillment`'s
   * `lineItemId`, NOT `id` (the prod_order_items UUID). Null on rows with no
   * Shopify line (not resolvable).
   */
  shopify_line_item_id?: number | null;
  /** Phase 60-02: standard product thumbnail (`skus.image_url`, org-scoped). */
  image_url?: string | null;
  /**
   * Phase 60-02: short-TTL (300s) signed URL for the re-hosted custom artwork
   * preview. Prefer over `image_url`; both can fail, so swap on error to a
   * placeholder (render as-is, no Shopify resize transform).
   */
  customPreviewUrl?: string | null;
}

/**
 * Blocked SKU info with deficit
 */
export interface BlockedSkuInfo {
  sku: string;
  product_name: string | null;
  deficit: number;
}

/**
 * Phase 57-07: per-SKU coverage at a candidate fulfillment location.
 */
export interface FulfillmentSuggestionItem {
  sku: string;
  needed: number;
  available: number;
  ok: boolean;
}

/**
 * Phase 57-07: smart all-SKU reroute suggestion. Mirrors the backend
 * `FulfillmentSuggestion`. Always fail-closed — `fulfillsAll` is true only on
 * verified, complete data; `dataComplete: false` means the UI must fall back to
 * the per-SKU routing rows rather than trust the suggestion.
 */
export interface FulfillmentSuggestion {
  fulfillsAll: boolean;
  locationId: string | null;
  locationName: string | null;
  totalSkus: number;
  coveredSkus: number;
  items: FulfillmentSuggestionItem[];
  dataComplete: boolean;
}

/**
 * Order detail response from backend
 * Note: Backend returns customer_name/customer_email directly, frontend should
 * minimize PII display (show initials, masked email)
 */
export interface OrderDetail {
  id: string;
  /**
   * BLU-563: the ERP `orders.id` (UUID), distinct from `id` (the prod_orders id).
   * The resolver (OrderResolutionFlow) sends this as `resolveOrderFulfillment`'s
   * `orderId`. Null for a standalone prod_order with no linked `orders` row
   * (cannot be resolved).
   */
  order_id: string | null;
  order_name: string | null;
  customer_name: string | null;
  customer_email: string | null;
  order_date: string | null;
  production_status: string;
  location_id: string;
  location_name: string;
  shopify_order_id: number | null;
  shipping_address1: string | null;
  shipping_address2: string | null;
  shipping_address_city: string | null;
  shipping_address_province: string | null;
  shipping_address_zip: string | null;
  shipping_address_country: string | null;
  line_items: LineItemDetail[];
  blocked_sku: string | null;
  blocked_skus: Array<{
    sku: string;
    deficit: number;
    stock_by_location: Omit<StockLevel, 'is_current_location'>[];
  }>;
  stock_by_location: Omit<StockLevel, 'is_current_location'>[];
  routing_suggestions: Omit<StockLevel, 'is_current_location'>[];
  /** Phase 57-07: smart all-SKU reroute suggestion; null when not computable. */
  fulfillment_suggestion: FulfillmentSuggestion | null;
}

/**
 * Helper to derive routing suggestions from stock levels.
 * Returns locations (excluding current) where stock >= deficit.
 */
export function getRoutingSuggestions(
  stockLevels: StockLevel[],
  deficit: number | undefined
): StockLevel[] {
  if (!deficit) return [];
  return stockLevels
    .filter((s) => s.available_quantity >= deficit && !s.is_current_location)
    .sort((a, b) => b.available_quantity - a.available_quantity);
}

/**
 * Helper to create customer display string (PII minimization).
 * Shows initials from name or masked email.
 */
export function getCustomerDisplay(
  customerName: string | null | undefined,
  customerEmail: string | null | undefined
): string {
  if (customerName) {
    const names = customerName.trim().split(/\s+/);
    if (names.length >= 2 && names[0] && names[names.length - 1]) {
      const firstInitial = names[0][0] ?? '';
      const lastInitial = names[names.length - 1]?.[0] ?? '';
      return `${firstInitial}.${lastInitial}.`.toUpperCase();
    }
    if (names.length === 1 && names[0] && names[0].length > 0) {
      return `${names[0][0]}.`.toUpperCase();
    }
  }
  if (customerEmail) {
    const [localPart, domain] = customerEmail.split('@');
    if (localPart && domain) {
      const masked = localPart.length > 1
        ? `${localPart[0]}***`
        : localPart;
      return `${masked}@${domain}`;
    }
    return customerEmail;
  }
  return 'Unknown';
}

/**
 * Helper to format shipping location (PII minimization).
 * Takes a full address and returns city/state/country only.
 */
export function getShippingLocationDisplay(address: string | null | undefined): string {
  if (!address) return 'Unknown';
  // If address contains commas, take the last 2-3 parts
  const parts = address.split(',').map((p) => p.trim());
  if (parts.length >= 3) {
    return parts.slice(-3).join(', ');
  }
  return address;
}

// ============================================================================
// OOS Filters Types
// ============================================================================

/**
 * OOS status filter options
 */
export type OosStatusFilter = 'all' | 'needs_decision' | 'awaiting_inventory' | 'routed';

/**
 * OOS source filter options
 * Note: Removed 'packing' - no PAP_DAMAGED issue type mapped to packing source
 * If packing OOS detection is added later, update this and the filter logic
 */
export type OosSourceFilter = 'all' | 'pre_batch' | 'picking';

/**
 * OOS age filter options
 */
export type OosAgeFilter = 'all' | 'today' | 'this_week' | 'older';

/**
 * OOS sort options
 */
export type OosSortOption = 'oldest' | 'newest' | 'order_number';

/**
 * OOS contacted filter options — whether the customer has been notified for the issue
 */
export type OosContactedFilter = 'all' | 'contacted' | 'not_contacted';

/**
 * Full filter state for OOS tab
 */
export interface OosFilterState {
  status: OosStatusFilter;
  source: OosSourceFilter;
  skuSearch: string;
  ageFilter: OosAgeFilter;
  sort: OosSortOption;
  search: string; // Customer name or order number
  contactedFilter: OosContactedFilter;
}

/**
 * Default filter state
 */
export const DEFAULT_OOS_FILTERS: OosFilterState = {
  status: 'all',
  source: 'all',
  skuSearch: '',
  ageFilter: 'all',
  sort: 'oldest',
  search: '',
  contactedFilter: 'all',
};

// ============================================================================
// Print File Issue Types
// ============================================================================

/**
 * Print file issue reason display config.
 * Must match PRINT_FILE_REASONS in PrintIssueDialog.tsx.
 */
export const PRINT_FILE_REASON_CONFIG: Record<
  string,
  { label: string; color: string }
> = {
  metadata_mismatch: { label: 'Metadata Mismatch', color: 'text-amber-700 bg-amber-100 border-amber-200' },
  wrong_design: { label: 'Wrong Design', color: 'text-orange-700 bg-orange-100 border-orange-200' },
  missing_artwork: { label: 'Missing Artwork', color: 'text-red-700 bg-red-100 border-red-200' },
  color_mismatch: { label: 'Color Mismatch', color: 'text-purple-700 bg-purple-100 border-purple-200' },
  low_resolution: { label: 'Low Resolution', color: 'text-amber-700 bg-amber-100 border-amber-200' },
  wrong_position: { label: 'Wrong Position', color: 'text-blue-700 bg-blue-100 border-blue-200' },
  // file_missing (self-heal) renders via the fileMissing-specific card path, not
  // this badge map; print_data_needed is a dual with no print_file_lookups row
  // (Feature B) — amber = needs operator input (red is owned by missing_artwork).
  print_data_needed: { label: 'Print Data Needed', color: 'text-amber-700 bg-amber-100 border-amber-200' },
  // Machine generated: a supply line whose shared-blank link is broken. Red =
  // stop, like missing_artwork; fixed at the link, never with an upload.
  supply_mapping_invalid: { label: 'Blank link broken', color: 'text-red-700 bg-red-100 border-red-200' },
  other: { label: 'Other Issue', color: 'text-gray-700 bg-gray-100 border-gray-200' },
};
